import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { CartProvider } from "@/hooks/useCart";
import { WishlistProvider } from "@/hooks/useWishlist";
import { RecentlyViewedProvider } from "@/hooks/useRecentlyViewed";
import { ComparisonBar, ComparisonProvider } from "@/components/product/ProductComparison";
import { ProtectedAdminRoute } from "@/components/admin/ProtectedAdminRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageTransition } from "@/components/layout/PageTransition";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { TranslationProvider } from "@/hooks/useTranslation";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { Suspense, lazy } from "react";
import { AnimatePresence } from "framer-motion";

// Eager load critical pages
import Index from "./pages/Index";

// Lazy load all other pages
const Shop = lazy(() => import("./pages/Shop"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const FlashSales = lazy(() => import("./pages/FlashSales"));
const Auth = lazy(() => import("./pages/Auth"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));
const Profile = lazy(() => import("./pages/Profile"));
const Orders = lazy(() => import("./pages/Orders"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const SharedWishlist = lazy(() => import("./pages/SharedWishlist"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const ReturnPolicy = lazy(() => import("./pages/ReturnPolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const About = lazy(() => import("./pages/About"));

// Admin Pages (Lazy)
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminCoupons = lazy(() => import("./pages/admin/AdminCoupons"));
const AdminNewsletter = lazy(() => import("./pages/admin/AdminNewsletter"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AdminAuditLogs"));
const AdminRoles = lazy(() => import("./pages/admin/AdminRoles"));
const AdminSupport = lazy(() => import("./pages/admin/AdminSupport"));
const AdminInventory = lazy(() => import("./pages/admin/AdminInventory"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminLoyalty = lazy(() => import("./pages/admin/AdminLoyalty"));
const AdminEmailLogs = lazy(() => import("./pages/admin/AdminEmailLogs"));
const AdminAbandonedCarts = lazy(() => import("./pages/admin/AdminAbandonedCarts"));
const AdminReferrals = lazy(() => import("./pages/admin/AdminReferrals"));
const AdminTestimonials = lazy(() => import("./pages/admin/AdminTestimonials"));
const AdminBundles = lazy(() => import("./pages/admin/AdminBundles"));
const AdminFlashSales = lazy(() => import("./pages/admin/AdminFlashSales"));
const AdminCMS = lazy(() => import("./pages/admin/AdminCMS"));
const AdminVariantTypes = lazy(() => import("./pages/admin/AdminVariantTypes"));
const AdminMedia = lazy(() => import("./pages/admin/AdminMedia"));

const queryClient = new QueryClient();

function AuthRedirect({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
}

const AppRoutes = () => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Public Routes */}
        <Route path="/" element={<PageTransition><Index /></PageTransition>} />
        <Route path="/shop" element={<PageTransition><Shop /></PageTransition>} />
        <Route path="/product/:slug" element={<PageTransition><ProductDetail /></PageTransition>} />
        <Route path="/auth" element={<PageTransition><AuthRedirect><Auth /></AuthRedirect></PageTransition>} />
        <Route path="/cart" element={<PageTransition><Cart /></PageTransition>} />
        <Route path="/checkout" element={<PageTransition><Checkout /></PageTransition>} />
        <Route path="/order-confirmation/:orderId" element={<PageTransition><OrderConfirmation /></PageTransition>} />
        
        {/* Feature-flagged public routes */}
        {FEATURE_FLAGS.flashSales && (
          <Route path="/flash-sales" element={<PageTransition><FlashSales /></PageTransition>} />
        )}
        {FEATURE_FLAGS.sharedWishlist && (
          <Route path="/wishlist/shared/:token" element={<PageTransition><SharedWishlist /></PageTransition>} />
        )}

        {/* Customer Routes */}
        <Route path="/profile" element={<PageTransition><Profile /></PageTransition>} />
        <Route path="/orders" element={<PageTransition><Orders /></PageTransition>} />
        <Route path="/wishlist" element={<PageTransition><Wishlist /></PageTransition>} />
        
        {/* Content Routes */}
        <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
        <Route path="/faq" element={<PageTransition><FAQ /></PageTransition>} />
        <Route path="/privacy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
        <Route path="/terms" element={<PageTransition><Terms /></PageTransition>} />
        <Route path="/returns" element={<PageTransition><ReturnPolicy /></PageTransition>} />
        <Route path="/about" element={<PageTransition><About /></PageTransition>} />

        {/* Admin Routes - Always Active */}
        <Route path="/admin" element={<ProtectedAdminRoute><PageTransition><Dashboard /></PageTransition></ProtectedAdminRoute>} />
        <Route path="/admin/products" element={<ProtectedAdminRoute><PageTransition><AdminProducts /></PageTransition></ProtectedAdminRoute>} />
        <Route path="/admin/orders" element={<ProtectedAdminRoute><PageTransition><AdminOrders /></PageTransition></ProtectedAdminRoute>} />
        <Route path="/admin/categories" element={<ProtectedAdminRoute><PageTransition><AdminCategories /></PageTransition></ProtectedAdminRoute>} />
        <Route path="/admin/coupons" element={<ProtectedAdminRoute><PageTransition><AdminCoupons /></PageTransition></ProtectedAdminRoute>} />
        <Route path="/admin/roles" element={<ProtectedAdminRoute><PageTransition><AdminRoles /></PageTransition></ProtectedAdminRoute>} />
        <Route path="/admin/support" element={<ProtectedAdminRoute><PageTransition><AdminSupport /></PageTransition></ProtectedAdminRoute>} />
        <Route path="/admin/inventory" element={<ProtectedAdminRoute><PageTransition><AdminInventory /></PageTransition></ProtectedAdminRoute>} />
        <Route path="/admin/email-logs" element={<ProtectedAdminRoute><PageTransition><AdminEmailLogs /></PageTransition></ProtectedAdminRoute>} />
        <Route path="/admin/testimonials" element={<ProtectedAdminRoute><PageTransition><AdminTestimonials /></PageTransition></ProtectedAdminRoute>} />
        <Route path="/admin/bundles" element={<ProtectedAdminRoute><PageTransition><AdminBundles /></PageTransition></ProtectedAdminRoute>} />
        <Route path="/admin/cms" element={<ProtectedAdminRoute><PageTransition><AdminCMS /></PageTransition></ProtectedAdminRoute>} />
        <Route path="/admin/media" element={<ProtectedAdminRoute><PageTransition><AdminMedia /></PageTransition></ProtectedAdminRoute>} />
        <Route path="/admin/variant-types" element={<ProtectedAdminRoute><PageTransition><AdminVariantTypes /></PageTransition></ProtectedAdminRoute>} />

        {/* Feature-flagged admin routes */}
        {FEATURE_FLAGS.adminAnalytics && (
          <Route path="/admin/analytics" element={<ProtectedAdminRoute><PageTransition><AdminAnalytics /></PageTransition></ProtectedAdminRoute>} />
        )}
        {FEATURE_FLAGS.adminReports && (
          <Route path="/admin/reports" element={<ProtectedAdminRoute><PageTransition><AdminReports /></PageTransition></ProtectedAdminRoute>} />
        )}
        {FEATURE_FLAGS.adminLoyalty && (
          <Route path="/admin/loyalty" element={<ProtectedAdminRoute><PageTransition><AdminLoyalty /></PageTransition></ProtectedAdminRoute>} />
        )}
        {FEATURE_FLAGS.adminAbandonedCarts && (
          <Route path="/admin/abandoned-carts" element={<ProtectedAdminRoute><PageTransition><AdminAbandonedCarts /></PageTransition></ProtectedAdminRoute>} />
        )}
        {FEATURE_FLAGS.adminReferrals && (
          <Route path="/admin/referrals" element={<ProtectedAdminRoute><PageTransition><AdminReferrals /></PageTransition></ProtectedAdminRoute>} />
        )}
        {FEATURE_FLAGS.adminNewsletter && (
          <Route path="/admin/newsletter" element={<ProtectedAdminRoute><PageTransition><AdminNewsletter /></PageTransition></ProtectedAdminRoute>} />
        )}
        {FEATURE_FLAGS.adminAuditLogs && (
          <Route path="/admin/audit-logs" element={<ProtectedAdminRoute><PageTransition><AdminAuditLogs /></PageTransition></ProtectedAdminRoute>} />
        )}
        {FEATURE_FLAGS.adminFlashSales && (
          <Route path="/admin/flash-sales" element={<ProtectedAdminRoute><PageTransition><AdminFlashSales /></PageTransition></ProtectedAdminRoute>} />
        )}
        
        {/* 404 */}
        <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TranslationProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <CartProvider>
              <WishlistProvider>
                <RecentlyViewedProvider>
                  <ComparisonProvider>
                  <ErrorBoundary>
                    <ScrollToTop />
                    <AppRoutes />
                    <ComparisonBar />
                    <MobileBottomNav />
                  </ErrorBoundary>
                  </ComparisonProvider>
                </RecentlyViewedProvider>
              </WishlistProvider>
            </CartProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </TranslationProvider>
  </QueryClientProvider>
);

export default App;
